import ShareSocialMedia from '@/components/core/shared/share.social.media'
import { Badge } from '@/components/ui/atoms/badge'
import { Button } from '@/components/ui/atoms/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/atoms/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/atoms/tooltip'
import { cn, getPrice } from '@/lib/client'
import type { ICombo, ICourse, IModule } from '@/types/product'
import { ArrowUpRight, CheckCircle2, Info, ShoppingBag } from 'lucide-react'

export default function ProductDetailsPrice({
  product,
  from = 0,
  to,
  handleClick,
}: {
  product: ICourse | ICombo | IModule
  from: number | null
  to: number
  handleClick: (ev: React.MouseEvent<HTMLButtonElement>) => void
}) {
  return (
    <Card
      className={cn(
        'border-b border-zinc-100 border-2 shadow-lg lg:w-96',
        product.percentage_off && 'border-emerald-500',
      )}
    >
      {product.percentage_off ? (
        <CardHeader className="p-4">
          <div className="flex justify-between items-center space-x-4">
            <Badge
              className={'bg-emerald-500'}
              variant="flag"
              bg="bg-emerald-500"
              border_r="border-r-emerald-500"
              border_t="border-t-emerald-500"
            >
              {Math.round(product.percentage_off)}% OFF
            </Badge>
          </div>
        </CardHeader>
      ) : (
        <div />
      )}
      {product.video ? (
        <CardHeader className="p-0">
          <video
            className={cn(
              'rounded-t-md',
              product.percentage_off && '-mt-[5.7rem]',
            )}
            title="Video de apresentação"
            controls
            src={`${product.video}#t=0.1`}
            preload="metadata"
          />
        </CardHeader>
      ) : (
        <div />
      )}
      <CardContent
        className={cn(
          'p-4 text-center',
          product.percentage_off && !product.video && '-mt-16',
        )}
      >
        {from ? (
          <div className="flex flex-col text-sm font-light">
            <span className="text-zinc-500">
              De <span className="line-through">{getPrice(from)}</span>
            </span>
            <span>Em até {product.quantityInstallments}x de </span>
            <strong
              className={cn(
                'text-4xl font-bold',
                product.percentage_off && 'text-emerald-500',
              )}
            >
              {getPrice(product.installmentValue)}
            </strong>
          </div>
        ) : (
          <div className="flex flex-col">
            <span className="text-xs leading-3">
              Em até {product.quantityInstallments}x de
            </span>
            <strong
              className={cn(
                'text-4xl font-bold',
                product.percentage_off && 'text-emerald-500',
              )}
            >
              {getPrice(product.installmentValue)}
            </strong>
          </div>
        )}
        <span className="text-xs">
          Ou{' '}
          <strong className={cn(product.percentage_off && 'text-emerald-500')}>
            {getPrice(to)}
          </strong>{' '}
          à vista com desconto
        </span>
      </CardContent>
      <CardContent className="px-4 py-2">
        <Button
          size={'sm'}
          className={cn(
            'rounded-sm w-full',
            product.type === 'module' && 'bg-sky-500 hover:bg-sky-500/90',
          )}
          onClick={handleClick}
        >
          <ShoppingBag size={18} className="mr-2" /> Comprar
        </Button>
      </CardContent>
      <CardContent className="flex flex-col items-start p-4 text-sm border-b">
        <ul className="w-full font-light space-y-2">
          <li className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2
                className={cn(
                  'text-primary',
                  product.type === 'module' && 'text-sky-500',
                )}
                size={16}
              />
              Professores qualificados
            </div>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-zinc-400 cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent className="text-xs" sideOffset={10}>
                  Nossos professores são profissionais já testados <br />e
                  aprovados em carreiras públicas concorridas <br />
                  e/ou especialistas em disciplinas específicas <br />
                  para concursos
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </li>
          <li className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2
                className={cn(
                  'text-primary',
                  product.type === 'module' && 'text-sky-500',
                )}
                size={16}
              />
              100% focado no edital
            </div>
          </li>
          {product.infiniteGuarantee && (
            <li className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2
                  className={cn(
                    'text-primary',
                    product.type === 'module' && 'text-sky-500',
                  )}
                  size={16}
                />
                Garantia infinita
              </div>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-zinc-400 cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent className="text-xs" sideOffset={10}>
                    Para cursos completos, nós garantimos <br />
                    atualização do conteúdo, sem custo <br />
                    adicional, até a data da prova. <br />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </li>
          )}
          {product.satisfactionGuarantee && (
            <li className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2
                  className={cn(
                    'text-primary',
                    product.type === 'module' && 'text-sky-500',
                  )}
                  size={16}
                />
                {product.satisfactionGuaranteeDays} dias de garantia
              </div>
            </li>
          )}
        </ul>
      </CardContent>
      <CardContent className="flex flex-col items-start p-4 text-sm gap-1 border-b">
        <div className="flex w-full justify-between gap-1">
          <strong>Edital</strong>
          <span>{product.publicTender.situation}</span>
        </div>
        {product.publicTender.date && (
          <div className="flex w-full justify-between gap-1">
            <strong>Data da Prova</strong>
            <span>{product.publicTender.date || 'A definir'}</span>
          </div>
        )}
        <div className="flex w-full justify-between gap-1">
          <strong>Concurso</strong>
          <span>{product.publicTender.name}</span>
        </div>
        {product.publicTender?.salary && product.publicTender.salary > 0 ? (
          <div className="flex w-full justify-between gap-1">
            <strong>Remuneração</strong>
            <span>
              {product.publicTender.salary
                ? `até ${getPrice(product.publicTender.salary)}`
                : 'A definir'}
            </span>
          </div>
        ) : (
          <></>
        )}
        <div className="flex w-full justify-between gap-1">
          <strong>Formação</strong>
          <span>{product.publicTender.formation}</span>
        </div>
        {product.publicTender.stand && (
          <div className="flex w-full justify-between gap-1">
            <strong>Banca</strong>
            <span>{product.publicTender.stand}</span>
          </div>
        )}
        <Button
          variant={'link'}
          asChild
          className={cn('w-full', product.type === 'module' && 'text-sky-500')}
        >
          <a
            href={`/concursos/${product.publicTender.permanentLink}`}
            target="_blank"
            aria-label="Assinaturas"
            rel="noreferrer"
          >
            Saiba mais
            <ArrowUpRight className="ml-1 h-4 w-4" />
          </a>
        </Button>
      </CardContent>
      <CardFooter className="flex flex-col items-center text-center px-4 pt-4 text-sm gap-3">
        <strong>Compartilhe</strong>
        <ShareSocialMedia
          link={`https://www.direcaoconcursos.com.br/cursos/${product.permanentLink}`}
          title={product?.name}
        />
      </CardFooter>
    </Card>
  )
}
